/**
 * Errores que registró Twilio en la cuenta (Monitor Alerts, últimos 30 días: Twilio no guarda
 * más). La columna «Al avisarle a Deenex» cuenta las veces que Twilio no pudo entregarnos un aviso
 * (el `request_url` es nuestro): si hay alguna, franja ámbar, porque sin esos avisos no se
 * registran entregas ni bajas. Nunca se muestra `alert_text` (puede traer datos de personas).
 */

import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import type { EstadisticasTwilio } from "../../types.estadisticas";
import { ACLARACION_ERRORES_TWILIO, TEXTO_FALLA_A_DEENEX, hayFallasADeenex } from "../../logic/estadisticas";
import { fechaHora, num } from "../../../campana-nueva/utils/format";
import { AvisoConectar, ErrorTarjeta, RUTA_CONFIG } from "./comun";

const NIVEL: Record<string, string> = { error: "Error", warning: "Advertencia", notice: "Aviso", debug: "Depuración" };

export function TarjetaErroresTwilio({ twilio }: { twilio: EstadisticasTwilio }) {
  if (!twilio.disponible) return <AvisoConectar motivo={twilio.motivoNoDisponible} />;
  const et = twilio.erroresTwilio;
  if (!et?.disponible) return <ErrorTarjeta texto={et?.motivo || "Twilio no devolvió los errores de la cuenta."} />;
  const filas = [...(et.errores || [])].sort((a, b) => (Number(b.cantidad) || 0) - (Number(a.cantidad) || 0));

  return (
    <div className="space-y-3">
      {hayFallasADeenex(filas) && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" role="alert">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <p className="flex-1">{TEXTO_FALLA_A_DEENEX}</p>
          <Link to={`${RUTA_CONFIG}?seccion=cuenta`} className="font-semibold whitespace-nowrap hover:underline">Ir a Configuración</Link>
        </div>
      )}
      {!filas.length ? (
        <p className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
          <CheckCircle2 size={15} className="text-emerald-500" /> Sin errores
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                <th scope="col" className="py-2 pr-3">Código</th>
                <th scope="col" className="py-2 pr-3">Nivel</th>
                <th scope="col" className="py-2 pr-3 text-right">Cantidad</th>
                <th scope="col" className="py-2 pr-3 text-right">Al avisarle a Deenex</th>
                <th scope="col" className="py-2 pr-3">Última vez</th>
                <th scope="col" className="py-2"><span className="sr-only">Documentación</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filas.map((f) => (
                <tr key={`${f.codigo}|${f.nivel}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs text-slate-700">{f.codigo || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{NIVEL[String(f.nivel).toLowerCase()] ?? f.nivel}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums font-semibold text-slate-900">{num(f.cantidad)}</td>
                  <td className={`py-2.5 pr-3 text-right tabular-nums ${f.aDeenex > 0 ? "font-semibold text-amber-700" : "text-slate-500"}`}>{num(f.aDeenex)}</td>
                  <td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap">{fechaHora(f.ultimaAt || undefined)}</td>
                  <td className="py-2.5 text-right">
                    {f.masInfo && (
                      <a href={f.masInfo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline whitespace-nowrap">
                        Ver en Twilio <ExternalLink size={11} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 leading-relaxed">{ACLARACION_ERRORES_TWILIO}</p>
    </div>
  );
}

export default TarjetaErroresTwilio;
